import { useProcess } from '@react-query/hooks/query/use-process';
import { useTranslation } from '@translations';
import { useMemo } from 'react';
import './process-last-activity.scss';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { useLinks } from '@hooks/use-links';
import { useProcessRole } from '@hooks/use-process-role';
import { formatDateStringToVerbose } from '@utils/date-utils';
import { useHashRoute } from '@utils/navigation-utils';
import { getLastActivityContent } from './get-last-activity-text-util';

export const ProcessLastActivity = () => {
    const { data: process, isSuccess } = useProcess();
    const { nextStep, smartspar } = useLinks();
    const { pendingProcess } = useTranslation();
    const { hasApplicantRole } = useProcessRole(process);

    type Action = keyof typeof pendingProcess.lastActivity.actions;

    const lastActivityContent = useMemo(() => {
        if (isSuccess) {
            return getLastActivityContent(process, hasApplicantRole, pendingProcess);
        }
    }, [isSuccess, process, hasApplicantRole, pendingProcess]);

    if (!lastActivityContent || !process) {
        return null;
    }

    const getLinkForAction = (action: Action) => {
        switch (action) {
            case 'smartsparReadMore':
                return smartspar.url;
            case 'completeKYC':
                return useHashRoute(`${nextStep.url}/${process.id}`);
        }
    };

    return (
        <section className="process-last-activity" data-testid="process-last-activity">
            <p className="process-last-activity__label" data-testid="last-activity-label">
                {`${pendingProcess.lastActivity.label} ${formatDateStringToVerbose(process.updated)}`}
            </p>
            <h2 className="process-last-activity__header" data-testid="last-activity-header">
                {lastActivityContent.header}
            </h2>
            <p className="process-last-activity__description" data-testid="last-activity-description">
                {lastActivityContent.description}
            </p>
            {lastActivityContent.action && (
                <Link
                    size="small"
                    linkType={
                        lastActivityContent.action === 'smartsparReadMore' ? 'button-positive' : 'button-negative'
                    }
                    className="process-last-activity__action"
                    data-testid="last-activity-action"
                    href={getLinkForAction(lastActivityContent.action)}
                    icon={lastActivityContent.action === 'smartsparReadMore' ? IconTypes.PIL_HOEYRE : undefined}
                    iconOnRightSide
                >
                    {pendingProcess.lastActivity.actions[lastActivityContent.action]}
                </Link>
            )}
        </section>
    );
};
